<template>
  <section v-if="eventOccurrences && Object.keys(eventOccurrences).length" :class="$style.frame">
    <header :class="$style.header">
      <h1 class="no-border-above">{{ title }}</h1>
      <div :class="$style.nav">
        <div>
          <div :class="[$style.link, { [$style.active]: view === 'day' }]">
            <router-link
              :to="{
                name: 'CalendarSection',
                params: {
                  date: selectedDate && selectedDate !== today ? selectedDate : '',
                  view: 'day',
                },
                query: activeFilters.length ? { q: activeFilters.join(',') } : null,
              }"
              :aria-current="view === 'day'"
              :disabled="view === 'day' ? true : null"
              :ga4-event="
                JSON.stringify({
                  click_type: 'view',
                  component: 'CalendarSection',
                  content_group: 'calendar',
                  gtm_tag: 'interaction',
                  link_text: 'Day',
                })
              "
            >
              Day View
            </router-link>
          </div>
          <div :class="[$style.link, { [$style.active]: view === 'month' }]">
            <router-link
              :to="{
                name: 'CalendarSection',
                params: {
                  date: selectedDate && selectedDate !== today ? selectedDate : '',
                  view: 'month',
                },
                query: activeFilters.length ? { q: activeFilters.join(',') } : null,
              }"
              :aria-current="view === 'month'"
              :disabled="view === 'month' ? true : null"
              :ga4-event="
                JSON.stringify({
                  click_type: 'view',
                  component: 'CalendarSection',
                  content_group: 'calendar',
                  gtm_tag: 'interaction',
                  link_text: 'Month',
                })
              "
            >
              Month View
            </router-link>
          </div>
        </div>
        <div :class="$style.selection">
          <div>
            <button
              id="datepicker-button"
              aria-controls="datepicker"
              :aria-expanded="selected === 'datepicker'"
              type="button"
              :disabled="datepickerDisabled"
              :class="['button-filter', selected === 'datepicker' ? 'up' : 'down']"
              @click.stop.prevent="toggle('datepicker')"
            >
              Select a Date
            </button>
            <Datepicker
              v-show="selected === 'datepicker'"
              id="datepicker"
              ref="datepickerRef"
              v-model:selected-date="selectedDate"
              :active-dates="activeDates"
              aria-labelledby="datepicker-button"
              :class="$style.datepicker"
              :collapsible="true"
              @close="selected = ''"
              @pick="setUserPick"
            ></Datepicker>
          </div>
          <div :class="['nav-subtypes', { active: selected === 'filters' }]">
            <button
              id="filters-button"
              aria-controls="filters"
              :aria-expanded="selected === 'filters'"
              type="button"
              :class="['button-filter', selected === 'filters' ? 'up' : 'down']"
              @click.stop.prevent="toggle('filters')"
            >
              Filters
            </button>
            <div
              v-show="selected === 'filters'"
              id="filters"
              ref="filterDropdown"
              aria-labelledby="filters-button"
              :class="[$style.navFilters, 'nav-items']"
            >
              <div v-for="(filter, key, index) in filters" :key="key">
                <input
                  :id="key"
                  v-model="activeFilters"
                  :aria-checked="isActiveFilter(key)"
                  :class="$style.input"
                  type="checkbox"
                  :value="key"
                  :ga4-event="
                    JSON.stringify({
                      click_type: 'link',
                      component: 'CalendarSection',
                      content_group: 'calendar',
                      gtm_tag: 'link',
                      link_text: 'See exhibitions on view',
                      link_url: '/exhibitions',
                    })
                  "
                />
                <label class="button" :for="key">
                  <div :class="$style.checkbox">
                    <IconCheck v-if="isActiveFilter(key)" :key="key"></IconCheck>
                    <IconBox v-else></IconBox>
                  </div>
                  <span>{{ filter }}</span>
                </label>
              </div>
              <button class="apply" type="button" @click="selected = ''">Close</button>
            </div>
          </div>
        </div>
      </div>
      <div v-if="selectedFilters.length || pickedDate" :class="$style.selectedFilters">
        <p class="label">Filtered by:</p>
        <div v-if="pickedDate">
          <input id="picked" v-model="checkedDate" type="checkbox" :value="pickedDate" />
          <label for="picked">{{ getDay(pickedDate) }}</label>
        </div>
        <div v-for="(filter, key) in selectedFilters" :key="key">
          <input
            :id="`input-${String(key)}`"
            v-model="selectedFilters"
            :checked="selectedFilters.indexOf(key) >= 0"
            type="checkbox"
            :value="filter"
          />
          <label :for="`input-${String(key)}`">
            {{ slugToTitle(filter) }}
          </label>
        </div>
        <button class="button-secondary" type="button" @click.prevent="clearAll">Clear</button>
      </div>
    </header>
    <CalendarDay
      v-if="view === 'day' && activeEvents.length"
      :class="$style.occurrences"
      :events="activeEvents"
      :first-date="firstDate"
    ></CalendarDay>
    <CalendarMonth
      v-if="view === 'month' && activeEvents.length"
      :class="$style.occurrences"
      :events="activeEvents"
      :first-date="firstDate"
    ></CalendarMonth>
    <p v-if="!activeEvents.length" class="space-24-32-above">
      There are no {{ noEventsFound() }} scheduled on or after
      {{ formatToString(parseISO(selectedDate)) }}.
    </p>
    <div v-if="activeEvents.length || !moreDisabled" :class="[$style.loadMore]">
      <p v-if="activeEvents.length && eventOccurrences.nodes.length" ref="total" class="body-2">
        Showing {{ activeEvents.length }} of {{ totalCount }} results
      </p>
      <button v-if="!moreDisabled" class="button-secondary" type="button" @click="loadData()">
        Load More Results
      </button>
    </div>
    <BackToTop></BackToTop>
  </section>
</template>

<script setup lang="ts">
import { AppConfig } from '@/types/global.interface.ts';
import BackToTop from '@/components/common/BackToTop.vue';
import CalendarDay from '@/components/calendar/CalendarDay.vue';
import CalendarMonth from '@/components/calendar/CalendarMonth.vue';
import { calendarQuery } from '@/queries/CalendarQuery';
import { computed, defineAsyncComponent, inject, nextTick, onMounted, ref, watch } from 'vue';
import { EventInterface } from '@/types/event.interface';
import { filters, monthNames } from '@/config/calendar';
import IconBox from '@/assets/svgs/IconBox.svg?component';
import IconCheck from '@/assets/svgs/IconCheck.svg?component';
import { slugToTitle } from '@/utils.js';
import {
  useA11y,
  useAnalytics,
  useDateFormat,
  useFilterInteractions,
  useHandleError,
  useMetaData,
} from '@/composables/Common.js';
import { useMainStore } from '@/stores/mainStore';
import { useQuery } from 'villus';
import { useRoute } from 'vue-router';

const Datepicker = defineAsyncComponent(() => import('@/components/calendar/DatePicker.vue'));

const props = defineProps<{
  date?: string;
  view?: string;
}>();

const { detectKeyboardUser } = useA11y();
const { formatToString } = useDateFormat();
const { ga4Event } = useAnalytics();
const { handleError } = useHandleError();
const { setMetaDescription, setMetaTitle } = useMetaData();
const route = useRoute();
const store = useMainStore();
const { setValue } = store;

const appConfig = inject<AppConfig>('appConfig');

let allActiveDates;

const activeDates = ref([]);
const activeEvents = ref([]);
const activeFilters = ref<string[]>([]);
const checkedDate = ref(false);
const datepickerDisabled = ref<boolean>(false);
const datepickerRef = ref(null);
const eventOccurrences = ref<{
  nodes: EventInterface[];
  pageInfo: {
    hasMoreEventOccurrences: boolean;
  };
}>({});
const filterDropdown = ref(null);
const moreDisabled = ref(false);
const moreSlices = ref(false);
const pickedDate = ref('');
const selected = ref('');
const selectedDate = ref(props.date);
const selectedFilters = ref<string[]>([]);
const sliceEnd = ref(20);
const sliceSize = ref(20);
const sliceStart = ref(0);
const totalCount = ref(0);

const { selectNav } = useFilterInteractions(selected);

const { data, error } = useQuery({
  onData: (data) => {
    setValue({ type: 'dataLoaded', value: true });
    setMetaTitle(title);
    eventOccurrences.value.nodes = [];
    eventOccurrences.value.pageInfo = data.eventOccurrences.pageInfo;

    if (data.eventOccurrences.nodes.length) {
      setMetaDescription(data.eventOccurrences.nodes[0].excerpt);
      Object.keys(data.eventOccurrences.nodes).map((key) => {
        // Build array of all valid active dates.
        if (
          !activeDates.value.includes(data.eventOccurrences.nodes[key].start.isoDate) &&
          data.eventOccurrences.nodes[key].start.isoDate >= today.value
        ) {
          activeDates.value.push(data.eventOccurrences.nodes[key].start.isoDate);
        }

        // Build array of all current event occurrences.
        if (data.eventOccurrences.nodes[key].start.isoDate >= today.value) {
          eventOccurrences.value.nodes.push(data.eventOccurrences.nodes[key]);
        }
      });
      allActiveDates = activeDates.value;
      loadData();
    }

    if (!data) {
      handleError(title, error);
    }

    nextTick().then(() => {
      detectKeyboardUser();
    });
  },
  query: calendarQuery,
  variables: {
    today: appConfig.today.isoDate,
  },
});

const firstDate = computed(() => {
  return selectedDate.value !== activeEvents.value[0].start.isoDate
    ? parseISO(selectedDate.value)
    : parseISO(activeEvents.value[0].start.isoDate);
});
const title = 'Calendar';
const today = computed(() => appConfig.today.isoDate);

watch(activeFilters, () => updateFilters());

watch(checkedDate, () => {
  handleDate();
});

/**
 * Watch for date changes, trigger navigation and send GA4 event.
 */
watch(selectedDate, () => {
  setUrl();
  resetSlice();
  loadData();
  ga4Event({
    filter_location: 'calendar',
    filter_type: 'date',
    filter_value: selectedDate.value.toString(),
    gtm_tag: 'filter',
  });
});

watch(selectedFilters, () => {
  activeFilters.value = selectedFilters.value;
  updateFilters();
});

onMounted(() => {
  /*
  ?filterSets=%7B"calendar"%3A%5B"Lectures%20%26amp%3B%20Symposia", "Concerts%20%26amp%3B%20Performances", "Special%20Events"%5D%7D
  */
  if (route.query.filterSets) {
    const legacyFilters = JSON.parse(route.query.filterSets as string).calendar;

    legacyFilters.map((filter) => {
      const current = filter.replace('&amp;', '');

      activeFilters.value.push(slugify(current));
      selectedFilters.value.push(slugify(current));
    });
  }

  /*
  ?q=lectures-symposia,concerts-performances,special-events
  */
  if (route.query.q) {
    route.query.q.split(',').map((filter) => {
      activeFilters.value.push(filter);
      selectedFilters.value.push(filter);
    });
  }

  pickedDate.value = route.params.date || '';
});

function isActiveFilter(key: string): boolean {
  return activeFilters.value.indexOf(key) >= 0;
}

function clearAll() {
  activeFilters.value = [];
  selectedFilters.value = [];
  handleDate();
}

function getDay(date: string): string {
  const selected = date.split('-');

  return `${monthNames[Number(selected[1]) - 1]} ${parseInt(selected[2])}`;
}

function handleDate() {
  if (selectedDate.value === pickedDate.value) {
    pickedDate.value = '';
  }

  selectedDate.value = today.value;
  setUrl(true);
  resetSlice();
  loadData();
}

/**
 * Loads the next slice of events.
 */
function loadData(): void {
  const temp = [];

  // Purge active events when there is a new slice.
  if (sliceStart.value === 0) {
    activeDates.value = [];
    activeEvents.value = [];
  }

  Object.keys(eventOccurrences.value.nodes).map((key) => {
    const event = eventOccurrences.value.nodes[key];

    if (activeFilters.value.length) {
      for (let i = 0; i < event.eventTypes.nodes.length; i++) {
        if (activeFilters.value.indexOf(event.eventTypes.nodes[i].slug) > -1) {
          // Filter active dates.
          if (
            !activeDates.value.includes(event.start.isoDate) &&
            event.start.isoDate >= today.value
          ) {
            activeDates.value.push(event.start.isoDate);
          }

          // Filter event occurrences.
          if (event.start.isoDate >= selectedDate.value) {
            temp.push(event);
            break;
          }
        }
      }
    }

    if (!activeFilters.value.length) {
      activeDates.value = allActiveDates;

      if (event.start.isoDate >= selectedDate.value) {
        temp.push(event);
      }
    }
  });

  // Conditionally disable datepicker.
  datepickerDisabled.value = activeDates.value.length === 0;

  if (moreSlices.value || (sliceStart.value === 0 && temp.length)) {
    // Display the current slice of event occurrences.
    activeEvents.value.push(...temp.slice(sliceStart.value, sliceEnd.value));
    moreSlices.value = sliceEnd.value < temp.length;
  }

  // Increment next slice bounds.
  if (moreSlices.value) {
    sliceStart.value = sliceEnd.value;
    sliceEnd.value =
      sliceEnd.value + sliceSize.value < temp.length
        ? sliceEnd.value + sliceSize.value
        : temp.length;

    ga4Event({
      click_type: 'load_more',
      component: 'events',
      content_group: 'calendar',
      gtm_tag: 'interaction',
      link_text: 'Load More Results',
    });
  }

  // Set load more button state.
  moreDisabled.value = !moreSlices.value;
  totalCount.value = temp.length;
}

/**
 * Generates the no events found message.
 */
function noEventsFound(): string {
  let str = '';

  for (let i = 0; i < activeFilters.value.length; i++) {
    str += filters[activeFilters.value[i]];

    if (activeFilters.value.length > 1 && i < activeFilters.value.length - 1) {
      str += ' or ';
    }
  }

  return str.slice(-6).toLowerCase() === 'events' ? str : `${str} events`;
}

function parseISO(date) {
  return new Date(`${date}T00:00:00`);
}

/**
 * Resets the slice start and end values.
 */
function resetSlice(): void {
  sliceStart.value = 0;
  sliceEnd.value = sliceSize.value;
}

/**
 * Sets, and navigates the user to, the target URL when filters or datepicker date is changed.
 */
function setUrl(isDeselected: boolean = false): void {
  const base = route.path.indexOf('calendar/month') > -1 ? '/calendar/month' : '/calendar/day';
  const date = isDeselected ? '' : selectedDate.value;

  let url = selectedDate.value && today.value !== selectedDate.value ? `${base}/${date}` : base;

  url = activeFilters.value.length ? url + '?q=' + activeFilters.value.join(',') : url;
  window.history.pushState({}, '', url);
}

function setUserPick(date: string): void {
  pickedDate.value = date;
}

function toggle(type: string): void {
  selectNav(type, [datepickerRef.value._.refs.datepicker, filterDropdown.value]);
  ga4Event({
    filter_location: 'calendar',
    filter_type: 'datepicker',
    filter_value: selected.value ? 'close' : 'open',
    gtm_tag: 'filter',
  });
}

function updateFilters(): void {
  if (JSON.stringify(selectedFilters.value) !== JSON.stringify(activeFilters.value)) {
    selectedFilters.value = activeFilters.value;
  }

  setUrl();
  resetSlice();
  loadData();
  ga4Event({
    filter_location: 'calendar',
    filter_type: 'filter',
    filter_value: activeFilters.value.join(','),
    gtm_tag: 'filter',
  });
}
</script>

<script lang="ts">
export default {
  beforeRoute(params, villusClient) {
    return new Promise<void>((resolve, reject) => {
      villusClient
        .executeQuery({
          query: calendarQuery,
          variables: {
            today: appConfig.today.isoDate,
          },
        })
        .then(() => resolve())
        .catch((err) => reject(err));
    });
  },
};
</script>

<style lang="scss">
@use '@/styles/links.module.scss' as *;
@use '@/styles/navigation.module.scss' as *;
</style>

<style lang="scss" module>
.checkbox {
  height: 24px;
  width: 24px;
}
.datepicker {
  position: absolute;
  left: -0.25rem;
  margin-top: px-to-rem(4);
  width: 100vw;
}
.header {
  &::after {
    @include line;

    & {
      margin-top: 1rem;
    }
  }
}

.load-more {
  @include space-48-64-above;

  button {
    margin: 1rem auto 0 auto;
    width: fit-content;
    display: block;
  }

  p {
    text-align: center;
  }
}

.link {
  margin-left: 1rem;

  &:first-of-type {
    margin-left: 0;
  }

  &.active {
    a {
      border-bottom: 2px solid #171717;
      cursor: text;

      &:focus,
      &:hover {
        text-decoration: none;
      }
    }
  }

  a {
    text-decoration: none;

    &:focus,
    &:hover {
      text-decoration: underline;
    }
  }
}

.nav {
  font-family: $guggensans;
  line-height: px-to-rem(20);
  margin: 2rem 0 1rem;
  padding: 0;
  width: fit-content;

  button {
    color: $black;
    cursor: pointer;

    &:hover {
      text-decoration: none;
    }
  }

  > div {
    cursor: pointer;
    list-style: none;
    margin-right: 1rem;

    > div {
      display: inline-block;
      width: fit-content;
    }

    &:first-of-type {
      margin-right: 0;
      text-align: center;
    }

    &:last-of-type {
      margin-right: 0;
      margin-top: 0.5rem;
    }
  }

  :global(.nav-subtypes) {
    &:global(.active) {
      :global(.nav-items) {
        border: 1px solid $black;
        left: 0;
        padding-bottom: 0;
        position: absolute;
        white-space: nowrap;
        width: calc(100vw - 2rem);

        :global(.apply) {
          position: sticky;
        }
      }
    }
  }
}

.selected-filters {
  margin-top: 4rem;
  padding-bottom: 1rem;

  &::before {
    @include line;

    & {
      margin-top: -2rem;
    }
  }

  div {
    display: inline-block;
    margin: 1rem 0.5rem 0 0;

    &:last-of-type {
      margin-right: 1rem;
    }

    input[type='checkbox'] {
      height: 0;
      opacity: 0;
      position: absolute;
      width: 0;

      &::after {
        display: none;
      }
    }

    label {
      text-decoration: underline;
    }
  }
}

.selected {
  background-color: $black;
}

.selection {
  position: relative;

  button {
    &:hover {
      text-decoration: underline;
    }
  }
}

@media (min-width: $break-point) {
  .datepicker {
    transform: none;
    width: px-to-rem(300);
  }

  .frame {
    @include grid-content;
  }

  .header {
    h1 {
      margin-top: 0;
    }
  }

  .header,
  .occurrences {
    grid-column: 1 / span 8;
  }

  .nav {
    align-items: center;
    display: grid;
    grid-auto-columns: max-content;
    grid-auto-flow: column;

    > div {
      &:first-of-type {
        margin-right: 1rem;
      }

      &:last-of-type {
        margin-top: 0;
      }

      > div {
        &.nav-filters {
          left: px-to-rem(115);
          top: px-to-rem(28);
          z-index: get-zindex(submenu);
        }
      }
    }

    :global(.nav-subtypes) {
      &:global(.active) {
        :global(.nav-items) {
          left: auto;
          width: fit-content;
        }
      }
    }
  }
}
</style>
